import { useMemo, useCallback } from 'react';
import { useApiBase } from './useApiBase';
import { listUsers, getUserDisplayName } from '../api/users';
import type { Activity } from '../types/activities';
import { useEntityLookup } from './useEntityLookup';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to batch-resolve user names for activities.
 * 
 * Resolves user names for activities that have user_id but missing user_name.
 * Returns a map of user_id -> user_name for efficient lookups.
 * 
 * Follows SOLID, DRY, and KISS principles by:
 * - Single responsibility: Only resolves user names
 * - Batch processing: One API call for all missing names
 * - Simple interface: Returns a lookup map
 */
export function useResolveActivityUserNames(activities: Activity[]): Map<string, string> {
  const apiBase = useApiBase();
  const { isAdmin } = useAuth();

  // Collect user IDs that need resolution from:
  // 1. Activity user_id fields
  // 2. User IDs in activity changes (owner_id, contact_id, assigned_to, etc.)
  const userIdsToResolve = useMemo(() => {
    const ids = new Set<string>();
    
    // User-related field patterns that should be resolved to user names
    // Note: We're specific about user fields to avoid resolving contact_id, deal_id, etc.
    const userFieldPatterns = [
      /^(owner|user|manager|technician|assignee|creator|updater|resolver)_id$/, // Specific user ID fields
      /^assigned_to/, // assigned_to (array of user IDs)
      /^created_by$/, // created_by
      /^updated_by$/, // updated_by
      /^resolved_by$/, // resolved_by
      /^project_manager_id$/, // project_manager_id
    ];

    const isUserField = (fieldName: string): boolean => {
      // Check against known user field patterns (roofing contractors are contacts, handled elsewhere)
      return userFieldPatterns.some(pattern => pattern.test(fieldName));
    };
    
    activities.forEach((activity) => {
      // 1. Resolve activity user_id
      const userId = activity.user_id;
      const userName = activity.user_name;
      
      if (userId && userId !== 'unknown' && (!userName || userName === userId)) {
        ids.add(userId);
      }
      
      // 2. Resolve user IDs in activity changes
      if (activity.changes) {
        Object.entries(activity.changes).forEach(([field, change]) => {
          // Check if this field is user-related
          if (isUserField(field)) {
            // Extract user IDs from old and new values
            const extractUserIds = (val: unknown): string[] => {
              if (!val || val === null || val === undefined || val === 'empty') {
                return [];
              }
              
              // If it's a string that looks like a user ID (MongoDB ObjectId format)
              if (typeof val === 'string' && val.length === 24 && /^[0-9a-fA-F]{24}$/.test(val)) {
                return [val];
              }
              
              // If it's an array (e.g., assigned_to)
              if (Array.isArray(val)) {
                return val.filter((item): item is string => 
                  typeof item === 'string' && item.length === 24 && /^[0-9a-fA-F]{24}$/.test(item)
                );
              }
              
              return [];
            };
            
            const oldIds = extractUserIds(change.old);
            const newIds = extractUserIds(change.new);
            
            [...oldIds, ...newIds].forEach(id => {
              if (id && id !== 'unknown') {
                ids.add(id);
              }
            });
          }
        });
      }
    });
    
    return Array.from(ids);
  }, [activities]);

  const loader = useCallback(
    async (ids: string[], signal: AbortSignal) => {
      // Skip API call for non-admin users (endpoint requires admin access)
      if (!isAdmin) {
        return new Map();
      }

      const users = await listUsers(apiBase);
      if (signal.aborted) {
        return new Map();
      }

      const map = new Map<string, string>();
      ids.forEach((userId) => {
        const user = users.find((u) => u.id === userId);
        if (!user) {
          return;
        }
        const name = getUserDisplayName(user);
        if (name && name !== userId) {
          map.set(userId, name);
        }
      });
      return map;
    },
    [apiBase, isAdmin],
  );

  return useEntityLookup(userIdsToResolve, loader);
}

/**
 * Utility function to get display name for an activity.
 * 
 * Rules:
 * - If user_id is null or "unknown" → "System"
 * - If user_name exists → use it
 * - If user_id exists but user_name is missing → try to resolve from map, fallback to "Unknown"
 * - Never return user_id directly
 */
export function getActivityUserDisplayName(
  activity: Activity,
  resolvedNames?: Map<string, string>
): string {
  const userId = activity.user_id;
  const userName = activity.user_name;

  // System activities: user_id is null or "unknown"
  if (!userId || userId === 'unknown') {
    return 'System';
  }

  // If user_name exists and is not the same as user_id, use it
  if (userName && userName !== userId) {
    return userName;
  }

  // Try to resolve from provided map
  if (resolvedNames && resolvedNames.has(userId)) {
    return resolvedNames.get(userId)!;
  }

  // Fallback: user-based activity but can't resolve name
  return 'Unknown';
}
